import { useState } from 'react';
import { Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import type { AdditionalAd, CreativeAsset, MetaCallToAction } from '../../../types/metaAdDraft';
import { CTA_OPTIONS } from '../ctaOptions';
import { AssetUploader } from './AssetUploader';
import { PublishStatusToggle } from './PublishStatusToggle';
import { VideoThumbnailField } from './VideoThumbnailField';
import { useAutoThumbnail } from '../useAutoThumbnail';
import type { UploadedImage } from '../useAssetUpload';

// Phase 2 — one additional-ad card (sequential attach). Each additional ad gets
// the FULL creative feature set: its own asset type (image | video — Phase 2a,
// independent per ad so mixed-media sets are valid), its own Feed asset,
// optional Story asset (placementAssets routing, same Phase 1 expand/remove
// pattern as the primary ad), and optional copy overrides (blank = inherit the
// shared value from the primary ad — the placeholder shows what's inherited).
// Video slots carry a REQUIRED thumbnail (no auto-extraction on attach).
//
// publishState (server-owned) drives the per-card status chip; a PUBLISHED ad's
// creative fields go read-only — it's live on Meta, edits here wouldn't propagate.

const EMPTY_ASSET: CreativeAsset = { type: null, url: null, path: null, thumbnailUrl: null, thumbnailPath: null };

// The shared copy an additional ad inherits field-by-field.
export type SharedAdCopy = {
  headline: string;
  primaryText: string;
  description: string;
  callToAction: MetaCallToAction;
  linkUrl: string;
};

const ctaLabel = (v: MetaCallToAction): string => CTA_OPTIONS.find((o) => o.value === v)?.label ?? v;

export function AdditionalAdCard({
  ordinal,
  value,
  shared,
  isLeadGen,
  accountId,
  onChange,
  onRemove,
}: {
  ordinal: number; // display number — "Ad 2", "Ad 3", …
  value: AdditionalAd;
  shared: SharedAdCopy;
  isLeadGen: boolean; // lead_generation ad set → no link URL on any ad
  accountId: string;
  onChange: (patch: Partial<AdditionalAd>) => void;
  onRemove: () => void;
}) {
  const [storyOpen, setStoryOpen] = useState(Boolean(value.storyAsset.url));
  const [confirmStoryRemove, setConfirmStoryRemove] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(value.copyOverride !== null);
  const [pendingAssetType, setPendingAssetType] = useState<'image' | 'video' | null>(null);

  const isPublished = value.publishState?.status === 'published';
  const isFailed = value.publishState?.status === 'failed';

  // Phase 2a — this card's own asset type (image | video), independent per ad.
  const assetType = value.assetType === 'video' ? 'video' : 'image';

  const slotToUploaded = (a: CreativeAsset): UploadedImage | null =>
    a.url && a.path ? { imageUrl: a.url, imagePath: a.path } : null;
  const setAsset = (slot: 'feedAsset' | 'storyAsset', next: UploadedImage | null) =>
    onChange({
      [slot]: next
        ? { type: assetType, url: next.imageUrl, path: next.imagePath, thumbnailUrl: null, thumbnailPath: null }
        : { ...EMPTY_ASSET },
    } as Partial<AdditionalAd>);
  // Video-only: set/clear a slot's REQUIRED thumbnail (no server-side
  // auto-extraction on attach — D3.4b fills it client-side on upload).
  const setAssetThumb = (slot: 'feedAsset' | 'storyAsset', next: UploadedImage | null) =>
    onChange({
      [slot]: {
        ...value[slot],
        thumbnailUrl: next?.imageUrl ?? null,
        thumbnailPath: next?.imagePath ?? null,
      },
    } as Partial<AdditionalAd>);

  // D3.4b — auto-extract a thumbnail from the local video file on upload (one
  // hook per video slot; applies only into an EMPTY thumbnail slot — and this
  // card's setAsset clears the thumbnail with a new video, so a fresh frame
  // always fills it).
  const feedAutoThumb = useAutoThumbnail({
    accountId,
    slot: value.feedAsset,
    apply: (img) => setAssetThumb('feedAsset', img),
  });
  const storyAutoThumb = useAutoThumbnail({
    accountId,
    slot: value.storyAsset,
    apply: (img) => setAssetThumb('storyAsset', img),
  });

  // Asset-type switch (image ↔ video) — clears BOTH this card's slots (Meta
  // can't mix image + video in one ad); confirmed first if anything is uploaded.
  const applyAssetType = (next: 'image' | 'video') =>
    onChange({ assetType: next, feedAsset: { ...EMPTY_ASSET }, storyAsset: { ...EMPTY_ASSET } });
  const requestAssetType = (next: 'image' | 'video') => {
    if (next === assetType) return;
    if (value.feedAsset.url || value.storyAsset.url) setPendingAssetType(next);
    else applyAssetType(next);
  };

  // The required-thumbnail area under a populated video slot (auto-extraction
  // states + the manual uploader as Replace).
  const thumbUploader = (slot: 'feedAsset' | 'storyAsset') => (
    <VideoThumbnailField
      accountId={accountId}
      slot={value[slot]}
      autoThumb={slot === 'feedAsset' ? feedAutoThumb : storyAutoThumb}
      required
      onManualChange={(img) => setAssetThumb(slot, img)}
      aspectGuidance={`matches the ${slot === 'feedAsset' ? 'Feed' : 'Story'} video ratio`}
    />
  );

  const showStory = storyOpen || Boolean(value.storyAsset.url);
  function requestStoryRemove() {
    if (value.storyAsset.url) setConfirmStoryRemove(true);
    else setStoryOpen(false);
  }
  function applyStoryRemove() {
    setAsset('storyAsset', null);
    setStoryOpen(false);
    setConfirmStoryRemove(false);
  }

  function requestRemove() {
    if (value.feedAsset.url || value.storyAsset.url) setConfirmRemove(true);
    else onRemove();
  }

  // Override fields: '' on the wire means "inherit" → stored as null. The whole
  // copyOverride collapses back to null via "Use shared copy".
  const o = value.copyOverride;
  const setOverride = (patch: Partial<NonNullable<AdditionalAd['copyOverride']>>) =>
    onChange({
      copyOverride: {
        headline: o?.headline ?? null,
        primaryText: o?.primaryText ?? null,
        description: o?.description ?? null,
        callToAction: o?.callToAction ?? null,
        linkUrl: o?.linkUrl ?? null,
        ...patch,
      },
    });
  const nullable = (v: string): string | null => (v.trim() === '' ? null : v);

  return (
    <div className="rounded-md border border-slate-200 p-4" data-testid={`additional-ad-${ordinal}`}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-slate-700">Ad {ordinal}</h4>
          {isPublished && (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
              Published ✓
            </span>
          )}
          {isFailed && (
            <span
              className="max-w-md truncate rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
              title={value.publishState?.error ?? undefined}
            >
              Failed — {value.publishState?.error ?? 'unknown error'}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={requestRemove}
          disabled={isPublished}
          title={isPublished ? 'This ad is live on Meta and can’t be removed from the draft.' : undefined}
          className="text-xs font-medium text-slate-500 hover:text-slate-700 hover:underline disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:no-underline"
        >
          Remove ad
        </button>
      </div>

      {/* Published ads are live on Meta — editing here wouldn't propagate. */}
      <fieldset
        disabled={isPublished}
        className={['m-0 min-w-0 space-y-4 border-0 p-0', isPublished ? 'pointer-events-none opacity-60' : ''].join(' ')}
      >
        <div className="max-w-xl">
          <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor={`additional-ad-name-${value.id}`}>
            Ad Name
          </label>
          <input
            id={`additional-ad-name-${value.id}`}
            type="text"
            value={value.name}
            onChange={(e) => onChange({ name: e.target.value })}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
          />
        </div>

        {/* Per-ad Active/Off — this ad's OWN status, independent of the ad set +
            campaign (a paused parent stops delivery but doesn't force this). */}
        <PublishStatusToggle
          label="Ad state"
          value={value.status}
          onChange={(status) => onChange({ status })}
        />

        {/* Phase 2a — this card's own asset type (D3.4 selector pattern). */}
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700">Asset type:</span>
            <div
              className="inline-flex overflow-hidden rounded-md border border-slate-300"
              role="radiogroup"
              aria-label={`Ad ${ordinal} asset type`}
            >
              {(['image', 'video'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  role="radio"
                  aria-checked={assetType === t}
                  onClick={() => requestAssetType(t)}
                  className={[
                    'px-3 py-1.5 text-sm font-medium transition-colors',
                    assetType === t ? 'bg-kyck-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
                  ].join(' ')}
                >
                  {t === 'image' ? 'Image' : 'Video'}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-400">
            Per ad — image and video ads can mix within the same ad set. This ad’s Feed and Story share the type.
          </p>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-700">Feed {assetType}</p>
          <AssetUploader
            kind={assetType}
            value={slotToUploaded(value.feedAsset)}
            onChange={(asset) => setAsset('feedAsset', asset)}
            accountId={accountId}
            aspectGuidance="1:1 (1080×1080) or 4:5 (1080×1350)"
            onFileSelected={assetType === 'video' ? feedAutoThumb.onVideoFileSelected : undefined}
          />
          {assetType === 'video' && value.feedAsset.url && thumbUploader('feedAsset')}
        </div>

        {/* Story asset — same Phase 1 inline expand pattern as the primary ad. */}
        {!showStory ? (
          <button
            type="button"
            onClick={() => setStoryOpen(true)}
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-3 py-1.5 text-sm font-medium text-kyck-700 hover:border-slate-400 hover:bg-slate-50"
          >
            <Plus className="h-4 w-4" /> Add Story-Specific Creative
          </button>
        ) : (
          <div className="space-y-3 rounded-md border border-slate-200 p-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-slate-700">Story {assetType}</p>
              <button
                type="button"
                onClick={requestStoryRemove}
                className="text-xs font-medium text-slate-500 hover:text-slate-700 hover:underline"
              >
                Remove Story-Specific Creative
              </button>
            </div>
            <p className="text-xs text-slate-400">
              9:16 vertical asset for Story and Reels placements. If not provided, the Feed asset will be used
              everywhere.
            </p>
            <AssetUploader
              kind={assetType}
              value={slotToUploaded(value.storyAsset)}
              onChange={(asset) => setAsset('storyAsset', asset)}
              accountId={accountId}
              aspectGuidance="9:16 (1080×1920)"
              onFileSelected={assetType === 'video' ? storyAutoThumb.onVideoFileSelected : undefined}
            />
            {assetType === 'video' && value.storyAsset.url && thumbUploader('storyAsset')}
          </div>
        )}

        {/* Copy — shared by default, per-field overrides optional. */}
        {!overrideOpen ? (
          <p className="text-sm text-slate-500">
            Using shared copy —{' '}
            <button
              type="button"
              onClick={() => setOverrideOpen(true)}
              className="font-medium text-kyck-700 hover:underline"
            >
              Override for this ad
            </button>
          </p>
        ) : (
          <div className="max-w-xl space-y-3 rounded-md border border-slate-200 p-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-slate-700">Copy overrides</p>
              <button
                type="button"
                onClick={() => {
                  onChange({ copyOverride: null });
                  setOverrideOpen(false);
                }}
                className="text-xs font-medium text-slate-500 hover:text-slate-700 hover:underline"
              >
                Use shared copy
              </button>
            </div>
            <p className="text-xs text-slate-400">Blank fields inherit the shared copy shown in the placeholder.</p>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor={`override-headline-${value.id}`}>
                Headline
              </label>
              <input
                id={`override-headline-${value.id}`}
                type="text"
                value={o?.headline ?? ''}
                placeholder={shared.headline || 'Shared headline'}
                onChange={(e) => setOverride({ headline: nullable(e.target.value) })}
                className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor={`override-primary-${value.id}`}>
                Primary text
              </label>
              <textarea
                id={`override-primary-${value.id}`}
                rows={3}
                value={o?.primaryText ?? ''}
                placeholder={shared.primaryText || 'Shared primary text'}
                onChange={(e) => setOverride({ primaryText: nullable(e.target.value) })}
                className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor={`override-description-${value.id}`}>
                Description
              </label>
              <input
                id={`override-description-${value.id}`}
                type="text"
                value={o?.description ?? ''}
                placeholder={shared.description || 'Shared description (optional)'}
                onChange={(e) => setOverride({ description: nullable(e.target.value) })}
                className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
              />
            </div>
            <div className="max-w-xs">
              <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor={`override-cta-${value.id}`}>
                Call to action
              </label>
              <select
                id={`override-cta-${value.id}`}
                value={o?.callToAction ?? ''}
                onChange={(e) =>
                  setOverride({
                    callToAction: e.target.value === '' ? null : (e.target.value as MetaCallToAction),
                  })
                }
                className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
              >
                <option value="">Shared ({ctaLabel(shared.callToAction)})</option>
                {CTA_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {!isLeadGen && (
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor={`override-url-${value.id}`}>
                  Link URL
                </label>
                <input
                  id={`override-url-${value.id}`}
                  type="url"
                  value={o?.linkUrl ?? ''}
                  placeholder={shared.linkUrl || 'Shared destination URL'}
                  onChange={(e) => setOverride({ linkUrl: nullable(e.target.value) })}
                  className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
                />
              </div>
            )}
          </div>
        )}
      </fieldset>

      {/* ---------- Asset-type switch confirmation (Phase 2a, D3.4 pattern) ---------- */}
      <Dialog open={pendingAssetType !== null} onOpenChange={(open) => { if (!open) setPendingAssetType(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Switch this ad to {pendingAssetType === 'video' ? 'video' : 'image'}?</DialogTitle>
            <DialogDescription>
              This will clear this ad’s uploaded {assetType === 'video' ? 'videos' : 'images'} (Feed and Story).
              You’ll need to upload {pendingAssetType === 'video' ? 'video' : 'image'} assets instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingAssetType(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (pendingAssetType) applyAssetType(pendingAssetType);
                setPendingAssetType(null);
              }}
            >
              Switch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Story-asset removal confirmation ---------- */}
      <Dialog open={confirmStoryRemove} onOpenChange={(open) => { if (!open) setConfirmStoryRemove(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove Story-specific creative?</DialogTitle>
            <DialogDescription>
              This won’t delete uploaded files, but they won’t be sent with the next publish.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmStoryRemove(false)}>
              Cancel
            </Button>
            <Button onClick={applyStoryRemove}>Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Remove-ad confirmation (only when an image is uploaded) ---------- */}
      <Dialog open={confirmRemove} onOpenChange={(open) => { if (!open) setConfirmRemove(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove Ad {ordinal}?</DialogTitle>
            <DialogDescription>
              This won’t delete uploaded files, but this ad won’t be published.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRemove(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConfirmRemove(false);
                onRemove();
              }}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
